/**
 * User-facing message for network / connectivity failures.
 * Replaces raw browser errors like "Failed to fetch" or TypeError text.
 */
export const NETWORK_ERROR_MESSAGE =
  'Unable to connect. Please check your internet connection and try again.';

const NETWORK_ERROR_PATTERNS = [
  'failed to fetch',
  'failed to load',
  'networkerror',
  'network request failed',
  'load failed',
  'typeerror',
  'timeout exceeded',
  'request timed out',
  'aborted',
  'err_internet_disconnected',
  'err_network_changed',
  'the internet connection appears to be offline',
];

/**
 * @param {*} error - RTK Query error, SerializedError, or plain object
 * @returns {boolean}
 */
export const isNetworkOrFetchError = (error) => {
  if (!error) return false;

  if (typeof error === 'string') {
    const lower = error.toLowerCase();
    return NETWORK_ERROR_PATTERNS.some((pattern) => lower.includes(pattern));
  }

  if (typeof error !== 'object') return false;

  const { status } = error;
  if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') {
    return true;
  }

  const raw =
    (typeof error.error === 'string' && error.error) ||
    (typeof error.message === 'string' && error.message) ||
    (typeof error.data?.message === 'string' && error.data.message) ||
    (typeof error.data === 'string' && error.data) ||
    '';

  if (!raw) return false;

  const lower = raw.toLowerCase();
  return NETWORK_ERROR_PATTERNS.some((pattern) => lower.includes(pattern));
};

/**
 * Normalize RTK Query baseQuery result so UI never shows raw fetch/TypeError text.
 * @param {{ data?: *, error?: *, meta?: * }} result
 * @returns {{ data?: *, error?: *, meta?: * }}
 */
export const normalizeApiErrorResult = (result) => {
  if (!result?.error || !isNetworkOrFetchError(result.error)) {
    return result;
  }

  const existingData =
    result.error.data && typeof result.error.data === 'object' && !Array.isArray(result.error.data)
      ? result.error.data
      : {};

  return {
    ...result,
    error: {
      ...result.error,
      error: NETWORK_ERROR_MESSAGE,
      data: {
        ...existingData,
        message: NETWORK_ERROR_MESSAGE,
      },
    },
  };
};

/**
 * Resolve a user-facing message from any API error shape (hooks, unwrap, catch).
 * @param {*} error
 * @param {string} [fallback='Something went wrong. Please try again.']
 * @returns {string}
 */
export const getApiErrorMessage = (
  error,
  fallback = 'Something went wrong. Please try again.'
) => {
  if (!error) return fallback;

  if (isNetworkOrFetchError(error)) {
    return NETWORK_ERROR_MESSAGE;
  }

  if (typeof error === 'string') {
    return isNetworkOrFetchError(error) ? NETWORK_ERROR_MESSAGE : error;
  }

  const apiMessage =
    error?.data?.message ||
    error?.data?.error ||
    (typeof error?.data === 'string' ? error.data : null);

  if (apiMessage && typeof apiMessage === 'string') {
    return apiMessage;
  }

  const genericMessage = error?.message || error?.error;
  if (typeof genericMessage === 'string' && genericMessage.trim()) {
    return isNetworkOrFetchError({ message: genericMessage })
      ? NETWORK_ERROR_MESSAGE
      : genericMessage;
  }

  return fallback;
};
